import React, { useRef, DragEvent, ChangeEvent } from 'react';
import { FileSelectionList } from './file-selection-list';
import { Button } from './button';
import { Upload } from 'lucide-react';
import { useDocumentStore } from '@/stores/useDocumentStore';
import { useToast } from '@/hooks/use-toast';
import { MAX_PDF_PAGES } from '@/domain/document-processing/utils/document-validation';

// Max number of files that can be uploaded
const MAX_FILES = 10;

interface OrderFileSectionProps {
  files: File[];
  onRemoveFile: (file: File) => void;
  onFilesSelected: (files: FileList) => void;
}

export function OrderFileSection({
  files,
  onRemoveFile,
  onFilesSelected
}: OrderFileSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const documentState = useDocumentStore();
  const { results, consolidationResult, isProcessing } = documentState;
  const filesLocked = isProcessing || results.length > 0 || !!consolidationResult;

  // Create a mapping of filename to document metadata
  const documentsMetadata = React.useMemo(() => {
    const metadata: Record<string, any> = {};

    // Helper function to remove extension
    const getBaseName = (filename: string) => filename.substring(0, filename.lastIndexOf('.')) || filename;

    console.log("Building document metadata map:", {
      hasConsolidationResult: !!consolidationResult,
      hasExtendedData: !!consolidationResult?.extendedData,
      hasDocumentFlow: !!consolidationResult?.extendedData?.documentFlow,
      docFlowLength: consolidationResult?.extendedData?.documentFlow?.timeline?.length || 0,
      resultsCount: results?.length || 0,
      fileNames: files.map(f => f.name)
    });

    // --- Prioritize using documentFlow timeline from consolidationResult (for historical data) ---
    if (consolidationResult?.extendedData?.documentFlow?.timeline) {
      // Map documentId (no extension) to the timeline entry
      const timelineMap = new Map<string, any>();
      consolidationResult.extendedData.documentFlow.timeline.forEach(item => {
        // Use documentId (which lacks extension) as the key
        timelineMap.set(item.documentId, item);
      });

      // Iterate through the source files listed in metadata (with extensions)
      consolidationResult.metadata?.sourceFiles?.forEach(fileName => {
        // Match base filename (no extension) against the map keys
        const baseName = getBaseName(fileName);
        const timelineEntry = timelineMap.get(baseName);

        if (timelineEntry) {
          // Use the *full original filename* (with extension) as the key in the final metadata map
          metadata[fileName] = {
            documentCategory: timelineEntry.documentType, // Get category from timeline
            documentDetails: timelineEntry // Pass the whole timeline entry for popup details
          };
        } else {
          console.warn(`No timeline entry found for base name: ${baseName} (from file: ${fileName})`);
        }
      });
      console.log('Built metadata from documentFlow.timeline:', metadata);
    }
    // --- Fallback for initial processing (if consolidationResult isn't ready/available yet) ---
    // Use the existing results array, acknowledging it lacks documentCategory
    else if (results && results.length > 0) {
      console.log('Building metadata using fallback results array');
      results.forEach(result => {
        if (result.fileName && !metadata[result.fileName]) { // Avoid overwriting if already mapped by timeline
          metadata[result.fileName] = {
            // documentCategory: undefined, // Explicitly undefined as it's missing
            documentDetails: { documentId: result.documentId } // Provide at least ID for potential linking
          };
        }
      });
    }

    return metadata;
  }, [results, consolidationResult, files]);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (filesLocked) return;

    if (e.dataTransfer.files) {
      const newFiles = e.dataTransfer.files;

      if (files.length + newFiles.length > MAX_FILES) {
        toast({
          title: "Maximum files exceeded",
          description: `You can upload a maximum of ${MAX_FILES} files. You currently have ${files.length} files.`,
          variant: "destructive",
        });
        return;
      }

      onFilesSelected(newFiles);
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (filesLocked) return;
    if (e.target.files) {
      const newFiles = e.target.files;

      if (files.length + newFiles.length > MAX_FILES) {
        toast({
          title: "Maximum files exceeded",
          description: `You can upload a maximum of ${MAX_FILES} files. You currently have ${files.length} files.`,
          variant: "destructive",
        });
        // Reset the input value
        e.target.value = '';
        return;
      }

      onFilesSelected(newFiles);
      // Reset the input value so the same file can be selected again
      e.target.value = '';
    }
  };

  return (
    <section aria-label="Order documents" className="space-y-4">
      <h2 className="text-base font-semibold">Documents</h2>
      {!isProcessing && filesLocked && <p className="text-xs text-muted-foreground">To verify a different set of documents, start a new order with all the files you need.</p>}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        disabled={filesLocked}
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={handleFileSelect}
      />

      {files.length === 0 ? (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className="group relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border p-6 text-center animate-in fade-in-50 hover:border-primary/50 hover:bg-accent/5 transition-colors cursor-pointer"
        >
          <div className="absolute inset-0" />
          <div className="mx-auto flex max-w-[420px] flex-col items-center justify-center text-center">
            <div className="mb-3 rounded-full bg-primary/10 p-3 transition-colors group-hover:bg-primary/20">
              <Upload className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-sm font-medium mb-1">Drag & drop files here</h3>
            <p className="text-sm text-muted-foreground mb-3">
              or click to upload
            </p>
            <p className="text-xs text-muted-foreground">
              Supported formats: PDF, JPG, PNG
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Maximum {MAX_FILES} files allowed, up to {MAX_PDF_PAGES} pages per PDF
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-muted-foreground">
              Selected Files ({files.length}/{MAX_FILES})
            </div>
            <div className="flex items-center gap-2">
              {files.length < MAX_FILES && !filesLocked && (
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={filesLocked}
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-muted-foreground hover:text-foreground"
                >
                  <Upload className="h-4 w-4" />
                  Add More Files
                </Button>
              )}
            </div>
          </div>
          <FileSelectionList
            files={files}
            onRemoveFile={onRemoveFile}
            documentsMetadata={documentsMetadata}
            readOnly={filesLocked}
          />
        </>
      )}
    </section>
  );
}
